import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./css/KuyumcuProfilPage.css";

import KuyumcuLeftSideNavbar from "./components/KuyumcuLeftSideNavbar";
import PremiumBackground from "./components/PremiumBackground";
import axiosInstance from "../../api/axiosInstance";
import { getUserId } from "../../utils/getUserId";
import { usePermission } from "../../context/PermissionContext";
import YetkisizErisim from "./components/YetkisizErisim";

// Eğer farklı bir path kullanıyorsan bunu kendi projenin yapısına göre düzelt.
// Senin paylaştığın util: formatNumberTR(value, fraction)
import { formatNumberTR } from "./utils/numberFormat";

/* ==========================================================
   PROFİL SAYFASI
   - Görüntüleme: PROFIL_VIEW
   - Düzenleme:   PROFIL_UPDATE
   - Alt kullanıcı kısıtları:
     - Firma Adı: disabled
     - Firma İl/İlçe/Adres: hiç görünmez
   - Devir Has Gram:
     - HERKES görür
     - SADECE KUYUMCU düzenler
     - TR format + 2 hane
   - Kur Alanları (Dolar/Euro/Altın):
     - HERKES görür
     - Düzenleme için: PROFIL_UPDATE yeterli (EKSTRA YETKİ KONTROLÜ YOK)
     - TR format + 2 hane
   - Not: Kur alanları double
========================================================== */

const parseNumberTR = (val) => {
  if (val === null || val === undefined) return 0;

  const s = String(val).trim();
  if (!s) return 0;

  // "5.000,50" -> "5000.50"
  const normalized = s.replace(/\./g, "").replace(",", ".");

  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
};

const KuyumcuProfilPage = () => {
  const navigate = useNavigate();

  const userId = getUserId();
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  const { can, isLoading } = usePermission();

  /* ===================== YETKİLER ===================== */
  const PERM_PROFIL_VIEW = "PROFIL_VIEW";
  const PERM_PROFIL_UPDATE = "PROFIL_UPDATE";

  const canView = !isLoading && can(PERM_PROFIL_VIEW);
  const canUpdate = !isLoading && can(PERM_PROFIL_UPDATE);

  /* ===================== STATE ===================== */
  const [loading, setLoading] = useState(true);

  // Profil datası (backend’den gelecek alanlar)
  const [userData, setUserData] = useState({
    kullaniciAdi: "",
    isim: "",
    soyisim: "",

    firmaAdi: "",
    firmaIl: "",
    firmaIlce: "",
    firmaAdres: "",

    mail: "",
    telNo: "",

    uyelik: "",
    ustKullaniciId: null, // alt kullanıcı tespiti için

    devirHasGram: 0,

    // Kurlar (double)
    dolarKur: 0,
    euroKur: 0,
    altinKur: 0,
  });

  // Devir input display (TR format)
  const [devirHasDisplay, setDevirHasDisplay] = useState("0,00");

  // Kur display (TR format) - 2 hane
  const [dolarKurDisplay, setDolarKurDisplay] = useState("0,00");
  const [euroKurDisplay, setEuroKurDisplay] = useState("0,00");
  const [altinKurDisplay, setAltinKurDisplay] = useState("0,00");

  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");

  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [downloadingBackup, setDownloadingBackup] = useState(false);

  /* ===================== DERIVED ===================== */
  const isAltKullanici = useMemo(() => {
    return (
      userData?.ustKullaniciId !== null &&
      userData?.ustKullaniciId !== undefined
    );
  }, [userData?.ustKullaniciId]);

  const canDownloadBackup = useMemo(() => {
    return userData?.uyelik === "SUPER_USER" || !isAltKullanici;
  }, [userData?.uyelik, isAltKullanici]);

  const uyelikLabel = useMemo(() => {
    const u = (userData.uyelik || "").toString().toUpperCase();
    if (!u) return "—";
    if (u === "KUYUMCU") return "Kuyumcu";
    if (u === "SUPER_USER") return "Süper Kullanıcı";
    if (u === "PERSONEL") return "Personel";
    if (u === "CANTACI") return "Çantacı";
    if (u === "ORTAK") return "Ortak";
    if (u === "BILINMEYEN") return "Bilinmeyen";
    return userData.uyelik;
  }, [userData.uyelik]);

  /* ===================== AUTH KONTROL ===================== */
  useEffect(() => {
    if (!userId || !token) navigate("/login");
  }, [userId, token, navigate]);

  /* ===================== PROFİL VERİSİ ===================== */
  useEffect(() => {
    if (!userId || !token) return;

    // Yetkiler yüklenmeden api'ye koşma
    if (isLoading) return;

    // VIEW yoksa profil datası çekme
    if (!can(PERM_PROFIL_VIEW)) {
      setLoading(false);
      return;
    }

    const loadProfile = async () => {
      try {
        setLoading(true);

        const resp = await axiosInstance.get(`/auth/profil/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = resp.data;

        const devir = data.devirHasGram ?? 0;

        // Kur alanları (double)
        const dolar = data.dolarKur ?? 0;
        const euro = data.euroKur ?? 0;
        const altin = data.altinKur ?? 0;

        setUserData({
          kullaniciAdi: data.kullaniciAdi || "",
          isim: data.isim || "",
          soyisim: data.soyisim || "",

          firmaAdi: data.firmaAdi || "",
          firmaIl: data.firmaIl || "",
          firmaIlce: data.firmaIlce || "",
          firmaAdres: data.firmaAdres || "",

          mail: data.mail || "",
          telNo: data.telNo || "",

          uyelik: data.uyelikTip || "",
          ustKullaniciId:
            data.ustKullaniciId === undefined ? null : data.ustKullaniciId,

          devirHasGram: devir,

          dolarKur: dolar,
          euroKur: euro,
          altinKur: altin,
        });

        // Display format (TR)
        setDevirHasDisplay(formatNumberTR(devir, 2));

        // Kur display format (TR) - 2 hane
        setDolarKurDisplay(formatNumberTR(dolar, 2));
        setEuroKurDisplay(formatNumberTR(euro, 2));
        setAltinKurDisplay(formatNumberTR(altin, 2));
      } catch (e) {
        console.error(e);
        alert("Profil bilgileri yüklenemedi");
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [userId, token, isLoading]); // can() değişebilir; isLoading yeterli

  /* ===================== PROFİL GÜNCELLE ===================== */
  const handleProfileSave = async () => {
    if (!userId || !token) return;
    if (isLoading) return;

    if (!can(PERM_PROFIL_UPDATE)) {
      alert("Profil düzenleme yetkiniz yok.");
      return;
    }

    try {
      setSavingProfile(true);

      // ⚠️ KRİTİK: Blur'a basmadan kaydet tıklanırsa state eski kalmasın diye
      // display değerlerinden her zaman parse edip payload'a yazıyoruz.
      const parsedDevir = parseNumberTR(devirHasDisplay);
      const parsedDolar = parseNumberTR(dolarKurDisplay);
      const parsedEuro = parseNumberTR(euroKurDisplay);
      const parsedAltin = parseNumberTR(altinKurDisplay);

      // İstersen kaydetmeden önce state'i de güncelle (görsel tutarlılık)
      setUserData((prev) => ({
        ...prev,
        devirHasGram: parsedDevir,
        dolarKur: parsedDolar,
        euroKur: parsedEuro,
        altinKur: parsedAltin,
      }));

      setDevirHasDisplay(formatNumberTR(parsedDevir, 2));
      setDolarKurDisplay(formatNumberTR(parsedDolar, 2));
      setEuroKurDisplay(formatNumberTR(parsedEuro, 2));
      setAltinKurDisplay(formatNumberTR(parsedAltin, 2));

      // Alt kullanıcı ise firma alanları backend’e gönderilmez (mail/telNo hariç)
      // Kur alanları için ekstra yetki yok dediğin için alt kullanıcı payload'ına da ekledim.
      const payload = isAltKullanici
        ? {
            mail: userData.mail,
            telNo: userData.telNo,
            dolarKur: parsedDolar,
            euroKur: parsedEuro,
            altinKur: parsedAltin,
          }
        : {
            firmaAdi: userData.firmaAdi,
            firmaIl: userData.firmaIl,
            firmaIlce: userData.firmaIlce,
            firmaAdres: userData.firmaAdres,
            mail: userData.mail,
            telNo: userData.telNo,

            // Devir sadece KUYUMCU düzenler (backend de böyle)
            ...(userData?.uyelik === "KUYUMCU"
              ? { devirHasGram: parsedDevir }
              : {}),

            // Kur alanları (double) - PROFIL_UPDATE yetkisi yeterli
            dolarKur: parsedDolar,
            euroKur: parsedEuro,
            altinKur: parsedAltin,
          };

      await axiosInstance.patch(`/auth/profil/${userId}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      alert("Profil bilgileri güncellendi");
    } catch (e) {
      console.error(e);
            const msg =
              e?.response?.data?.message ||
              e?.response?.data ||
              "Profil güncellenemedi";
            alert(msg);
    } finally {
      setSavingProfile(false);
    }
  };

  /* ===================== ŞİFRE GÜNCELLE ===================== */
  const handlePasswordChange = async () => {
    if (!userId || !token) return;
    if (isLoading) return;

    if (!can(PERM_PROFIL_UPDATE)) {
      alert("Şifre güncelleme için yetkiniz yok.");
      return;
    }

    if (!newPassword || !newPasswordConfirm) {
      alert("Lütfen tüm alanları doldurun");
      return;
    }

    if (newPassword !== newPasswordConfirm) {
      alert("Şifreler eşleşmiyor");
      return;
    }

    if (newPassword.length < 8) {
      alert("Şifre en az 8 karakter olmalıdır.");
      return;
    }

    try {
      setSavingPassword(true);

      await axiosInstance.patch(
        `/auth/profil/${userId}/password`,
        {
          newPassword,
          newPasswordConfirm,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      alert("Şifre başarıyla güncellendi");
      setNewPassword("");
      setNewPasswordConfirm("");
    } catch (e) {
      console.error(e);
            const msg =
              e?.response?.data?.message ||
              e?.response?.data ||
              "Şifre güncellenemedi";
            alert(msg);
    } finally {
      setSavingPassword(false);
    }
  };

  /* ===================== SQL YEDEK İNDİR ===================== */
  const handleBackupDownload = async () => {
    if (!token) {
      alert("Oturum bilgisi bulunamadı.");
      return;
    }

    if (!canDownloadBackup) {
      alert("Firma yedeğini yalnızca firma yöneticisi alabilir.");
      return;
    }

    try {
      setDownloadingBackup(true);

      const response = await axiosInstance.get("/backup/download", {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const contentType =
        response.headers?.["content-type"] || "application/sql";

      const contentDisposition =
        response.headers?.["content-disposition"] || "";

      let fileName = `prosetwin-yedek-${new Date()
        .toISOString()
        .replaceAll(":", "-")
        .slice(0, 19)}.sql`;

      const utf8Match = contentDisposition.match(
        /filename\*=UTF-8''([^;]+)/i
      );
      const normalMatch = contentDisposition.match(
        /filename="?([^";]+)"?/i
      );

      if (utf8Match?.[1]) {
        fileName = decodeURIComponent(utf8Match[1]);
      } else if (normalMatch?.[1]) {
        fileName = normalMatch[1];
      }

      const blob = new Blob([response.data], {
        type: contentType,
      });

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Yedek indirme hatası:", error);

      let message = "Veritabanı yedeği oluşturulamadı.";
      const errorData = error?.response?.data;

      if (errorData instanceof Blob) {
        try {
          const errorText = await errorData.text();
          const parsed = JSON.parse(errorText);
          message = parsed?.message || parsed?.error || message;
        } catch {
          // Varsayılan mesaj korunur.
        }
      } else {
        message =
          errorData?.message ||
          errorData ||
          error?.message ||
          message;
      }

      alert(message);
    } finally {
      setDownloadingBackup(false);
    }
  };

  /* ===================== VIEW YETKİSİ YOKSA ===================== */
  if (!isLoading && !canView) {
    return (
      <div className="profil-container">
        <PremiumBackground />
        <KuyumcuLeftSideNavbar />
        <div className="profil-content">
          <YetkisizErisim />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="profil-container">
{/*         <PremiumBackground /> */}
        <KuyumcuLeftSideNavbar />
        <div className="profil-content">
          <div className="profil-loading">Bilgiler yükleniyor…</div>
        </div>
      </div>
    );
  }

  /* ===================== UI ===================== */
  return (
    <div className="profil-container">
{/*       <PremiumBackground /> */}
      <KuyumcuLeftSideNavbar />

      <div className="profil-content">
        {/* PANELLER */}
        <div className="profil-grid">

          {/* VERİTABANI YEDEĞİ KARTI */}
          {canDownloadBackup && (
            <div className="profil-card profil-card-wide profil-backup-card">
              <div className="profil-card-header">
                <div className="profil-card-title">
                  <span className="profil-badge-icon">💾</span>
                  Veritabanı Yedeği
                </div>

                <div className="profil-card-desc">
                  {userData?.uyelik === "SUPER_USER"
                    ? "Tüm veritabanı; tablo yapıları, sequence değerleri ve kayıtlarla birlikte SQL olarak indirilir."
                    : "Firmanıza ve firmanıza bağlı alt kullanıcılara ait kayıtlar SQL dosyası olarak indirilir."}
                </div>
              </div>

              <div className="profil-card-body">
                <div className="profil-backup-info">
                  <div className="profil-backup-icon">SQL</div>

                  <div>
                    <div className="profil-backup-title">
                      {userData?.uyelik === "SUPER_USER"
                        ? "Tam Veritabanı Yedeği"
                        : "Firma Verileri Yedeği"}
                    </div>

                    <div className="profil-backup-text">
                      {userData?.uyelik === "SUPER_USER"
                        ? "Bu yedek sistemdeki tüm firmaları, kullanıcıları, tabloları ve verileri kapsar."
                        : "Bu yedek yalnızca firmanıza ait hesapları, işlemleri, stokları, logları, parametreleri ve alt kullanıcıları kapsar."}
                    </div>
                  </div>
                </div>

                <div className="profil-security-note">
                  <div className="profil-security-dot" />
                  <div>
                    SQL dosyası hassas veriler içerebilir. Dosyayı güvenli bir
                    yerde saklayın ve yetkisiz kişilerle paylaşmayın.
                  </div>
                </div>

                <div className="profil-inline-actions profil-backup-actions">
                  <button
                    type="button"
                    className="profil-backup-btn"
                    onClick={handleBackupDownload}
                    disabled={
                      downloadingBackup ||
                      savingProfile ||
                      savingPassword
                    }
                  >
                    {downloadingBackup
                      ? "Veritabanı Hazırlanıyor…"
                      : "Veritabanı Yedeğini İndir"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

          {/* PROFİL KARTI */}
          <div className="profil-card">
            <div className="profil-card-header">
              <div className="profil-card-title">
                <span className="profil-badge-icon">👤</span>
                Profil Bilgileri
              </div>
              <div className="profil-card-desc">
                Firma, e-posta ve telefon bilgilerinizi güncelleyin.
              </div>
            </div>

            <div className="profil-card-body">
              <div className="profil-form-grid">
                {/* Kullanıcı Adı (disabled) */}
                <div className="profil-field">
                  <label>Kullanıcı Adı</label>
                  <input value={userData.kullaniciAdi} disabled />
                  <div className="profil-hint">Kullanıcı adı değiştirilemez.</div>
                </div>

                {/* İsim (disabled) */}
                <div className="profil-field">
                  <label>İsim</label>
                  <input value={userData.isim} disabled />
                </div>

                {/* Soyisim (disabled) */}
                <div className="profil-field">
                  <label>Soyisim</label>
                  <input value={userData.soyisim} disabled />
                </div>

                {/* Firma Adı (alt kullanıcı: disabled) */}
                <div className="profil-field">
                  <label>Firma Adı</label>
                  <input
                    value={userData.firmaAdi}
                    onChange={(e) =>
                      setUserData({ ...userData, firmaAdi: e.target.value })
                    }
                    placeholder="Firma adınız"
                    disabled={
                      !canUpdate ||
                      savingProfile ||
                      savingPassword ||
                      isAltKullanici
                    }
                    title={
                      !canUpdate
                        ? "Düzenleme yetkiniz yok."
                        : isAltKullanici
                        ? "Alt kullanıcı firma adını değiştiremez."
                        : ""
                    }
                  />
                </div>

                {/* Firma İl / İlçe / Adres (ALT KULLANICI GÖRMEZ) */}
                {!isAltKullanici && (
                  <>
                    <div className="profil-field">
                      <label>Firma İl</label>
                      <input
                        value={userData.firmaIl}
                        onChange={(e) =>
                          setUserData({ ...userData, firmaIl: e.target.value })
                        }
                        placeholder="İl"
                        disabled={!canUpdate || savingProfile || savingPassword}
                        title={!canUpdate ? "Düzenleme yetkiniz yok." : ""}
                      />
                    </div>

                    <div className="profil-field">
                      <label>Firma İlçe</label>
                      <input
                        value={userData.firmaIlce}
                        onChange={(e) =>
                          setUserData({ ...userData, firmaIlce: e.target.value })
                        }
                        placeholder="İlçe"
                        disabled={!canUpdate || savingProfile || savingPassword}
                        title={!canUpdate ? "Düzenleme yetkiniz yok." : ""}
                      />
                    </div>

                    <div className="profil-field profil-field-wide">
                      <label>Firma Adres</label>
                      <input
                        value={userData.firmaAdres}
                        onChange={(e) =>
                          setUserData({
                            ...userData,
                            firmaAdres: e.target.value,
                          })
                        }
                        placeholder="Adres"
                        rows={3}
                        disabled={!canUpdate || savingProfile || savingPassword}
                        title={!canUpdate ? "Düzenleme yetkiniz yok." : ""}
                      />
                    </div>
                  </>
                )}

                {/* Devir Has Gram (HERKES GÖRÜR - SADECE KUYUMCU DÜZENLER) */}
                <div className="profil-field">
                  <label>Devir Has Gram</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={devirHasDisplay}
                    onChange={(e) => setDevirHasDisplay(e.target.value)}
                    onFocus={() => {
                      const raw = (userData.devirHasGram ?? 0).toString();
                      setDevirHasDisplay(raw.replace(".", ","));
                    }}
                    onBlur={() => {
                      const parsed = parseNumberTR(devirHasDisplay);
                      setUserData((prev) => ({ ...prev, devirHasGram: parsed }));
                      setDevirHasDisplay(formatNumberTR(parsed, 2));
                    }}
                    placeholder="0,00"
                    disabled={
                      !canUpdate ||
                      savingProfile ||
                      savingPassword ||
                      userData?.uyelik !== "KUYUMCU"
                    }
                    title={
                      !canUpdate
                        ? "Düzenleme yetkiniz yok."
                        : userData?.uyelik !== "KUYUMCU"
                        ? "Bu alan yalnızca firma yöneticisi (Kuyumcu) tarafından düzenlenebilir."
                        : ""
                    }
                  />
                  {userData?.uyelik !== "KUYUMCU" && (
                    <div className="profil-hint">
                      Bu alan firma devri has gram bilgisidir ve yalnızca firma
                      yöneticisi tarafından güncellenir.
                    </div>
                  )}
                </div>

                {/* Dolar Kur */}
                <div className="profil-field">
                  <label>Dolar Kuru</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={dolarKurDisplay}
                    onChange={(e) => setDolarKurDisplay(e.target.value)}
                    onFocus={() => {
                      const raw = (userData.dolarKur ?? 0).toString();
                      setDolarKurDisplay(raw.replace(".", ","));
                    }}
                    onBlur={() => {
                      const parsed = parseNumberTR(dolarKurDisplay);
                      setUserData((prev) => ({ ...prev, dolarKur: parsed }));
                      setDolarKurDisplay(formatNumberTR(parsed, 2));
                    }}
                    placeholder="0,00"
                    disabled={!canUpdate || savingProfile || savingPassword}
                    title={!canUpdate ? "Düzenleme yetkiniz yok." : ""}
                  />
                </div>

                {/* Euro Kur */}
                <div className="profil-field">
                  <label>Euro Kuru</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={euroKurDisplay}
                    onChange={(e) => setEuroKurDisplay(e.target.value)}
                    onFocus={() => {
                      const raw = (userData.euroKur ?? 0).toString();
                      setEuroKurDisplay(raw.replace(".", ","));
                    }}
                    onBlur={() => {
                      const parsed = parseNumberTR(euroKurDisplay);
                      setUserData((prev) => ({ ...prev, euroKur: parsed }));
                      setEuroKurDisplay(formatNumberTR(parsed, 2));
                    }}
                    placeholder="0,00"
                    disabled={!canUpdate || savingProfile || savingPassword}
                    title={!canUpdate ? "Düzenleme yetkiniz yok." : ""}
                  />
                </div>

                {/* Altın Kur */}
                <div className="profil-field">
                  <label>Has Fiyatı</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={altinKurDisplay}
                    onChange={(e) => setAltinKurDisplay(e.target.value)}
                    onFocus={() => {
                      const raw = (userData.altinKur ?? 0).toString();
                      setAltinKurDisplay(raw.replace(".", ","));
                    }}
                    onBlur={() => {
                      const parsed = parseNumberTR(altinKurDisplay);
                      setUserData((prev) => ({ ...prev, altinKur: parsed }));
                      setAltinKurDisplay(formatNumberTR(parsed, 2));
                    }}
                    placeholder="0,00"
                    disabled={!canUpdate || savingProfile || savingPassword}
                    title={!canUpdate ? "Düzenleme yetkiniz yok." : ""}
                  />
                </div>

                {/* Email */}
                <div className="profil-field">
                  <label>E-Mail</label>
                  <input
                    type="email"
                    value={userData.mail}
                    onChange={(e) =>
                      setUserData({ ...userData, mail: e.target.value })
                    }
                    placeholder="ornek@firma.com"
                    disabled={!canUpdate || savingProfile || savingPassword}
                    title={!canUpdate ? "Düzenleme yetkiniz yok." : ""}
                  />
                </div>

                {/* Telefon */}
                <div className="profil-field">
                  <label>Telefon</label>
                  <input
                    value={userData.telNo}
                    onChange={(e) =>
                      setUserData({ ...userData, telNo: e.target.value })
                    }
                    placeholder="05xx xxx xx xx"
                    disabled={!canUpdate || savingProfile || savingPassword}
                    title={!canUpdate ? "Düzenleme yetkiniz yok." : ""}
                  />
                </div>
              </div>

              <div className="profil-divider" />

              <div className="profil-inline-actions">
                {canUpdate && (
                  <button
                    className="profil-primary-btn"
                    onClick={handleProfileSave}
                    disabled={savingProfile || savingPassword || downloadingBackup}
                    title="Profil bilgilerini kaydet"
                  >
                    {savingProfile ? "Kaydediliyor…" : "Bilgileri Kaydet"}
                  </button>
                )}

                {canUpdate && (
                  <button
                    className="profil-ghost-btn"
                    onClick={() => window.location.reload()}
                    disabled={savingProfile || savingPassword || downloadingBackup}
                    title="Değişiklikleri geri al"
                  >
                    Değişiklikleri Geri Al
                  </button>
                )}
              </div>

              {!canUpdate && (
                <div className="profil-hint" style={{ marginTop: 10 }}>
                  Bilgileri güncellemek için “Profil Sayfası Düzenleme” yetkisi
                  gerekir.
                </div>
              )}
            </div>
          </div>

          {/* ÜYELİK KARTI */}
          <div className="profil-card profil-card-accent">
            <div className="profil-card-header">
              <div className="profil-card-title">
                <span className="profil-badge-icon">🪙</span>
                Üyelik & Paket
              </div>
              <div className="profil-card-desc">
                Mevcut üyelik tipinizi burada görürsünüz.
              </div>
            </div>

            <div className="profil-card-body">
              <div className="profil-stat">
                <div className="profil-stat-label">Mevcut Üyelik</div>
                <div className="profil-stat-value">{uyelikLabel}</div>
                <div className="profil-stat-note">
                  Paket içeriği/yetki kapsamı “Kullanıcı Yetkileri” ekranından
                  yönetilir.
                </div>
              </div>

              <div className="profil-divider" />

              <div className="profil-tip">
                <div className="profil-tip-title">Bilgi</div>
                <div className="profil-tip-text">
                  Alt kullanıcıların ekran erişimleri, sistemde tanımlı yetki
                  kodlarına göre kısıtlanır.
                </div>
              </div>
            </div>
          </div>

          {/* GÜVENLİK KARTI */}
          <div className="profil-card profil-card-wide">
            <div className="profil-card-header">
              <div className="profil-card-title">
                <span className="profil-badge-icon">🔒</span>
                Güvenlik (Şifre Değiştir)
              </div>
              <div className="profil-card-desc">
                Şifrenizi düzenli aralıklarla güncellemeniz önerilir.
              </div>
            </div>

            <div className="profil-card-body">
              <div className="profil-form-grid profil-form-grid-2">
                <div className="profil-field">
                  <label>Yeni Şifre</label>
                  <input
                    type="password"
                    placeholder="En az 8 karakter"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={!canUpdate || savingPassword || savingProfile || downloadingBackup}
                    title={!canUpdate ? "Düzenleme yetkiniz yok." : ""}
                  />
                </div>

                <div className="profil-field">
                  <label>Yeni Şifre (Tekrar)</label>
                  <input
                    type="password"
                    placeholder="Yeni şifreyi tekrar yazın"
                    value={newPasswordConfirm}
                    onChange={(e) => setNewPasswordConfirm(e.target.value)}
                    disabled={!canUpdate || savingPassword || savingProfile || downloadingBackup}
                    title={!canUpdate ? "Düzenleme yetkiniz yok." : ""}
                  />
                </div>
              </div>

              <div className="profil-security-note">
                <div className="profil-security-dot" />
                <div>
                  Şifre, harf ve rakam içeren güçlü bir kombinasyon olmalıdır.
                  Şifrenizi kimseyle paylaşmayın.
                </div>
              </div>

              <div className="profil-inline-actions">
                {canUpdate && (
                  <button
                    className="profil-danger-btn"
                    onClick={handlePasswordChange}
                    disabled={savingPassword || savingProfile || downloadingBackup}
                  >
                    {savingPassword ? "Güncelleniyor…" : "Şifreyi Güncelle"}
                  </button>
                )}

                {canUpdate && (
                  <button
                    className="profil-ghost-btn"
                    onClick={() => {
                      setNewPassword("");
                      setNewPasswordConfirm("");
                    }}
                    disabled={savingPassword || savingProfile || downloadingBackup}
                  >
                    Temizle
                  </button>
                )}
              </div>

              {!canUpdate && (
                <div className="profil-hint" style={{ marginTop: 10 }}>
                  Şifre değiştirmek için “Profil Sayfası Düzenleme” yetkisi
                  gerekir.
                </div>
              )}
            </div>
          </div>



        {/* ALT BİLGİ */}
        <div className="profil-footer-note">
          Bu ekrandaki değişiklikler, kaydedildiğinde ilgili kullanıcı hesabına
          uygulanır.
        </div>
      </div>
    </div>
  );
};

export default KuyumcuProfilPage;